import React, { useState, useEffect, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX, FORM_INLINE_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import AddressSearchModal from '../../component/AddressSearchModal'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatDate } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import AnimalPackageSharingFields from '../animalPackages/AnimalPackageSharingFields'
import AnimalShippingPolicyFields from './AnimalShippingPolicyFields'
import {
    DEFAULT_ANIMAL_SHIPPING_POLICY,
    normalizeAnimalShippingPolicy,
    validateAnimalShippingPolicy,
} from './animalShippingPolicy'
import {
    ANIMAL_IMAGE_FILE_ACCEPT,
    ANIMAL_IMAGE_FILE_ERROR_MESSAGE,
    applyAnimalDraft,
    clearAnimalDraft,
    createAnimalDraftPayload,
    createDefaultSelectionOption,
    createEditAnimalPackageDraftKey,
    createEditAnimalDraftKey,
    isAnimalImageFile,
    normalizeAnimalSelectionOption,
    readAnimalDraft,
    saveAnimalDraft,
} from './animalDraftUtils'
import {
    ANIMAL_IMAGE_LEAVE_MESSAGE,
    hasPendingAnimalImages,
    usePendingAnimalImageUnloadGuard,
} from './animalLeaveGuard'
import {
    ANIMAL_PACKAGE_CODE,
    ANIMAL_PACKAGE_REQUIRED_AREA_M2,
    SEOUL25_REGIONS,
    buildAnimalPackageRegions,
    createDefaultAnimalPackageState,
    isAnimalPackageDraftPayload,
    isValidAnimalPackageCode,
    isSeoul25CategoryRows,
    normalizeAnimalPackageCode,
    normalizeAnimalPackageStateFromDetail,
    sortSeoul25CategoryRows,
    validateAnimalPackageRegions,
} from '../animalPackages/animalPackageUtils'

const NO_IMAGE = '/images/noimage.jpg'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultData = () => ({
    sOptionName: '',
    ...DEFAULT_ANIMAL_SHIPPING_POLICY,
    ...createDefaultAnimalPackageState(),
})

const formSectionSx = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 1.5,
    boxShadow: 'none',
}

const imagePreviewCardSx = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    height: { xs: 220, md: 260 },
}

const imagePreviewSx = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
}

const imageUploadPanelSx = {
    ...FORM_SUB_PANEL_SX,
    p: 1.25,
    minWidth: 0,
}

const imageCardHeaderSx = {
    minHeight: 24,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    columnGap: 1,
}

const courseRowSx = {
    mt: 1.5,
    p: 1.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

const optionStatusCardSx = (selected) => ({
    m: 0,
    p: 1.25,
    alignItems: 'flex-start',
    borderRadius: '4px',
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'rgba(148, 163, 184, 0.28)',
    bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : '#fff',
    '& .MuiRadio-root': {
        mt: -0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

const detailSummarySx = {
    p: 2,
    mb: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
}

const detailSummaryTitleSx = {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 0.5,
}

const detailSummaryMainSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
    },
    alignItems: 'stretch',
    gap: 1.5,
}

const detailSummarySalesColumnSx = {
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 1,
}

const detailSummarySalesSx = {
    minWidth: 0,
    p: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'primary.main',
    backgroundColor: 'action.selected',
}

const detailSummaryStatusSx = {
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(3, minmax(0, 1fr))',
    },
    gap: 0.75,
}

const detailSummaryPeriodSx = {
    mt: 2,
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
    },
    gap: 1,
}

const detailPeriodRowSx = {
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: '58px minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: 1,
    p: 1,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

const statusPillSx = ({ active, danger = false }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 28,
    px: 1.25,
    borderRadius: '999px',
    fontSize: 12,
    fontWeight: 700,
    color: active ? 'primary.dark' : danger ? 'error.dark' : 'text.secondary',
    backgroundColor: active ? 'action.selected' : danger ? 'error.light' : 'divider',
})

const OPTION_STATUS_OPTIONS = [
    {
        value: 0,
        title: '미사용',
        description: '옵션을 운영 화면에서 비활성 상태로 관리합니다.',
    },
    {
        value: 1,
        title: '사용',
        description: '옵션 입력값을 사용 상태로 저장합니다.',
    },
]

const VISIBILITY_OPTIONS = [
    { value: 1, label: '노출' },
    { value: 0, label: '미노출' },
]

// 긴 애니멀런 폼을 운영 목적별 카드로 나눠 등록/수정 화면의 흐름을 맞춘다.
function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...formSectionSx, ...sx }}>
            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(148, 163, 184, 0.24)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            {children}
        </Paper>
    )
}

function OptionStatusSetting({ label, value, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {OPTION_STATUS_OPTIONS.map((option) => {
                    const checked = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio checked={checked} onChange={() => onChange(option.value)} />}
                            sx={optionStatusCardSx(checked)}
                            label={(
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {option.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.description}
                                    </Typography>
                                </Box>
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function StatusPill({ active, danger, children }) {
    return (
        <Box component="span" sx={statusPillSx({ active, danger })}>
            {children}
        </Box>
    )
}

function DetailPeriodRow({ label, value }) {
    return (
        <Box sx={detailPeriodRowSx}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, wordBreak: 'keep-all' }}>
                {value}
            </Typography>
        </Box>
    )
}

const getAnimalMeetTypeLabel = (meetType2) => {
    switch (Number(meetType2)) {
        case 1:
            return '애니멀런'
        case 2:
            return '댕댕이레이스'
        case 3:
            return '산넘어산'
        case 4:
            return 'B급마라톤'
        default:
            return '타입 미설정'
    }
}

function AnimalDetailSummary({
    data,
    beginDay,
    finishDay,
    totalLimitQuantity,
    totalAvailableQuantity,
    formatQuantity,
}) {
    const isShown = Number(data.isShow) === 1
    const canPay = Number(data.paymentStatus) === 1
    const canCoupon = Number(data.couponStatus) === 1
    const receptionPeriod = `${formatDate(data.startTime, { parseString: true })} ~ ${formatDate(data.endTime, { parseString: true })}`
    const eventPeriod = `${formatDate(beginDay, { parseString: true })} ~ ${formatDate(finishDay, { parseString: true })}`
    const meetTypeLabel = getAnimalMeetTypeLabel(data.meetType2)

    return (
        <Box sx={detailSummarySx}>
            <Box sx={detailSummaryMainSx}>
                <Box sx={detailSummaryTitleSx}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        {meetTypeLabel}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 0, color: 'text.primary', fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>
                        {data.title || '대회명 없음'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        수정 전 판매 상태와 노출 상태를 먼저 확인하세요.
                    </Typography>
                </Box>
                <Box sx={detailSummarySalesColumnSx}>
                    <Box sx={detailSummarySalesSx}>
                        <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                            판매 가능
                        </Typography>
                        <Typography sx={{ mt: 0.25, color: 'primary.dark', fontSize: { xs: 28, md: 34 }, fontWeight: 800, lineHeight: 1 }}>
                            {formatQuantity(totalAvailableQuantity)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            제한 {formatQuantity(totalLimitQuantity)} / 결제 {formatQuantity(data.netSoldQuantity)} / 입금대기 {formatQuantity(data.waitingDepositQuantity)}
                        </Typography>
                    </Box>
                    <Box sx={detailSummaryStatusSx}>
                        <StatusPill active={isShown} danger={!isShown}>{isShown ? '노출 중' : '미노출'}</StatusPill>
                        <StatusPill active={canPay} danger={!canPay}>{canPay ? '결제 가능' : '결제 불가'}</StatusPill>
                        <StatusPill active={canCoupon}>{canCoupon ? '쿠폰 가능' : '쿠폰 불가'}</StatusPill>
                    </Box>
                </Box>
            </Box>
            <Box sx={detailSummaryPeriodSx}>
                <DetailPeriodRow
                    label="접수"
                    value={receptionPeriod}
                />
                <DetailPeriodRow
                    label="대회"
                    value={eventPeriod}
                />
            </Box>
        </Box>
    )
}

// 이미지 미리보기와 숨김 file input을 한 카드로 묶어 세 이미지 행 레이아웃을 재사용한다.
function ImageUploadCard({
    title,
    image,
    imageAlt,
    inputId,
    inputName,
    uploadType,
    onUpload,
    canClear = false,
    hasPendingFile = false,
    onClear,
    disabled = false,
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...imageUploadPanelSx }}>
            <Box sx={imageCardHeaderSx}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {canClear && hasPendingFile && !disabled && (
                    <Button size="small" color="error" variant="text" startIcon={<RemoveIcon />} onClick={onClear} sx={FORM_IMAGE_CLEAR_BUTTON_SX}>
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={imagePreviewCardSx}>
                <Box component="img" src={image || NO_IMAGE} alt={imageAlt} sx={imagePreviewSx}/>
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: ANIMAL_IMAGE_FILE_ACCEPT }}
                    style={{ display: "none" }}
                    name={inputName}
                    id={inputId}
                    type="file"
                    onChange={e => onUpload(e, uploadType)}
                    disabled={disabled}
                />
                <Box component="label" htmlFor={disabled ? undefined : inputId} sx={{ display: 'block' }}>
                    <Button fullWidth variant="contained" color="success" component="span" startIcon={<Add />} disabled={disabled} sx={FORM_WIDE_UPLOAD_BUTTON_SX}>
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
        </Box>
    )
}

export default function Animal(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.productId
    const packageMode = Boolean(props.packageMode)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchMeetType2: '', searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = packageMode ? createEditAnimalPackageDraftKey(id) : createEditAnimalDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState(() => createDefaultData())
    const [beginDay, setBeginDay] = useState(new Date())
    // const [beginHours, setBeginHours] = useState(12)
    // const [beginMinutes, setBeginMinutes] = useState(0)
    const [finishDay, setFinishDay] = useState(new Date())
    // const [finishHours, setFinishHours] = useState(12)
    // const [finishMinutes, setFinishMinutes] = useState(0)
    const [mainImage, setMainImage] = useState('')
    const [subImage1, setSubImage1] = useState('')
    const [recordImage, setRecordImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [arr, setArr] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [options, setOptions] = useState([])
    const [selectionOptions, setSelectionOptions] = useState([])
    const initialDraftSnapshotRef = useRef('')
    const baseImagePreviewRef = useRef({
        mainImage: NO_IMAGE,
        subImage1: NO_IMAGE,
        recordImage: NO_IMAGE,
    })
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingAnimalImages(data) && !saving && !navigationUnlocked

    const toNumber = (value) => Number(value || 0)
    const formatQuantity = (value) => `${formatNumber(value)}개`
    const totalLimitQuantity = arr.reduce((sum, item) => sum + toNumber(item.quantity), 0)
    const totalAvailableQuantity = Math.max(totalLimitQuantity - toNumber(data.netSoldQuantity) - toNumber(data.waitingDepositQuantity), 0)
    const getQuantitySummaryText = (limitQuantity, netSoldQuantity, waitingDepositQuantity = 0) => (
        `판매가능 ${formatQuantity(Math.max(toNumber(limitQuantity) - toNumber(netSoldQuantity) - toNumber(waitingDepositQuantity), 0))}`
    )

    usePendingAnimalImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 애니멀런 상세를 불러올 수 없습니다.'
                setData(createDefaultData())
                setArr([])
                setOptions([])
                setSelectionOptions([])
                setMainImage(NO_IMAGE)
                setSubImage1(NO_IMAGE)
                setRecordImage(NO_IMAGE)
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/animal/getAnimal', {productId: id})
                if (data.code === 1) {
                    const baseDetailData = data.data || { sOptionName: '' }
                    const packageState = packageMode
                        ? {
                            ...normalizeAnimalPackageStateFromDetail(baseDetailData),
                            isAnimalPackage: true,
                        }
                        : createDefaultAnimalPackageState()
                    const baseData = {
                        ...baseDetailData,
                        ...normalizeAnimalShippingPolicy(baseDetailData),
                        ...packageState,
                    }

                    if (!baseData.productId) {
                        setData(createDefaultData())
                        setArr([])
                        setOptions([])
                        setSelectionOptions([])
                        setMainImage(NO_IMAGE)
                        setSubImage1(NO_IMAGE)
                        setRecordImage(NO_IMAGE)
                        setDetailErrorMessage('애니멀런 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const baseArr = packageMode ? sortSeoul25CategoryRows(data.categorys || []) : data.categorys || []
                    const baseOptions = baseData.product_option || []
                    const baseSelectionOptions = Array.isArray(baseData.sOptions) ? baseData.sOptions : []
                    const baseBeginDay = new Date(baseData.beginTime)
                    const safeBeginDay = Number.isNaN(baseBeginDay.getTime()) ? new Date() : baseBeginDay
                    const baseFinishDay = new Date(baseData.finishTime)
                    const safeFinishDay = Number.isNaN(baseFinishDay.getTime()) ? new Date() : baseFinishDay
                    let storedDraft = readAnimalDraft(draftKey)

                    if (packageMode && storedDraft && !isSeoul25CategoryRows(storedDraft.arr)) {
                        clearAnimalDraft(draftKey)
                        storedDraft = null
                    } else if (!packageMode && isAnimalPackageDraftPayload(storedDraft)) {
                        clearAnimalDraft(draftKey)
                        storedDraft = null
                    }

                    const restoredDraft = applyAnimalDraft({
                        baseData,
                        baseArr,
                        baseOptions,
                        baseSelectionOptions,
                        beginDay: safeBeginDay,
                        finishDay: safeFinishDay,
                        draft: storedDraft,
                    })

                    if (packageState.isAnimalPackage) {
                        restoredDraft.data.isAnimalPackage = true
                    }

                    initialDraftSnapshotRef.current = JSON.stringify(createAnimalDraftPayload({
                        data: baseData,
                        arr: baseArr,
                        options: baseOptions,
                        selectionOptions: baseSelectionOptions,
                        beginDay: safeBeginDay,
                        finishDay: safeFinishDay,
                    }))

                    setData(restoredDraft.data)
                    setArr(packageMode ? sortSeoul25CategoryRows(restoredDraft.arr) : restoredDraft.arr)
                    setOptions(restoredDraft.options)
                    setSelectionOptions(restoredDraft.selectionOptions)
                    setHasDetail(Boolean(baseData?.productId))
                    const nextMainImage = baseData?.mainImage === null ? NO_IMAGE : baseData?.mainImage?.includes('animal') ?
                        `${process.env.REACT_APP_AWS_S3_URL}/${baseData.mainImage}` : `${process.env.REACT_APP_SERVER_URL}/product/${baseData.adminId}/${baseData.mainImage}`
                    const nextSubImage1 = baseData?.subImage1 === null ? NO_IMAGE : baseData?.subImage1?.includes('animal') ?
                        `${process.env.REACT_APP_AWS_S3_URL}/${baseData.subImage1}` : `${process.env.REACT_APP_SERVER_URL}/product/${baseData.adminId}/${baseData.subImage1}`
                    const nextRecordImage = baseData?.recordImage === null ? NO_IMAGE : baseData?.recordImage?.includes('animal') ?
                        `${process.env.REACT_APP_AWS_S3_URL}/${baseData.recordImage}` : `${process.env.REACT_APP_SERVER_URL}/product/${baseData.adminId}/${baseData.recordImage}`

                    baseImagePreviewRef.current = {
                        mainImage: nextMainImage,
                        subImage1: nextSubImage1,
                        recordImage: nextRecordImage,
                    }
                    setMainImage(nextMainImage)
                    setSubImage1(nextSubImage1)
                    setRecordImage(nextRecordImage)
                    setBeginDay(restoredDraft.beginDay)
                    setFinishDay(restoredDraft.finishDay)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '애니멀런 상세를 불러오지 못했습니다.'
                    setData(createDefaultData())
                    setArr([])
                    setOptions([])
                    setSelectionOptions([])
                    setMainImage(NO_IMAGE)
                    setSubImage1(NO_IMAGE)
                    setRecordImage(NO_IMAGE)
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '애니멀런 상세를 불러오지 못했습니다.'
                setData(createDefaultData())
                setArr([])
                setOptions([])
                setSelectionOptions([])
                setMainImage(NO_IMAGE)
                setSubImage1(NO_IMAGE)
                setRecordImage(NO_IMAGE)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, packageMode])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 애니멀런 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createAnimalDraftPayload({
            data,
            arr,
            options,
            selectionOptions,
            beginDay,
            finishDay,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAnimalDraft(draftKey)
            return
        }

        saveAnimalDraft(draftKey, nextDraft)
    }, [arr, beginDay, data, draftKey, draftReady, finishDay, hasDetail, options, selectionOptions])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 저장/뒤로 이동 후에도 운영자가 보던 목록 조건으로 그대로 복귀시킨다.
    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAnimalDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: packageMode ? '/animal-packages' : '/animals',
                search: listSearch,
                searchText: searchText,
                searchSelect: searchSelect,
                curPage: curPage,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {})
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(ANIMAL_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        moveToList()
    }

    const addInput = () => {
        setArr(s => {
            return [
                ...s,
                {
                    id: arr[arr.length - 1].id + 1,
                    type: 2,
                    distance: 0,
                    price: 30000,
                    name: '',
                    quantity: 200,
                    status: 0,
                    order: arr[arr.length - 1].order + 1
                }
            ]
        })
    }

    const removeInput = (i) => {
        setArr(arr.filter((item, index) => index !== i))
    }

    const addSelectionOption = () => {
        setSelectionOptions((prevOptions) => [
            ...prevOptions,
            createDefaultSelectionOption(prevOptions.length),
        ])
    }

    const removeSelectionOption = (index) => {
        setSelectionOptions((prevOptions) => prevOptions.filter((_, itemIndex) => itemIndex !== index))
    }

    const updateSelectionOption = (index, name, value) => {
        setSelectionOptions((prevOptions) => prevOptions.map((item, itemIndex) => (
            itemIndex === index ? { ...item, [name]: value } : item
        )))
    }

    const getSubmittedCategoryRows = () => {
        const baseRows = arr.map((item, index) => ({
            ...item,
            status: 0,
            order: index,
            webVisible: Number(item.webVisible ?? 1),
            appVisible: Number(item.appVisible ?? 1),
        }))

        if (!Number(data.sStatus)) {
            return baseRows
        }

        const optionRows = selectionOptions.map((item, index) => {
            const option = normalizeAnimalSelectionOption(item, index)

            return {
                ...option,
                name: option.name.trim(),
                distance: 0,
                type: 2,
                status: 1,
                order: baseRows.length + index,
                webVisible: Number(option.webVisible ?? 1),
                appVisible: Number(option.appVisible ?? 0),
            }
        })

        return [...baseRows, ...optionRows]
    }

    const onChangeOptionType = (e, index) => {
        const value = e.target.value
        let newArr = [...arr]

        if (value === 0) {
            newArr[index].type = 0
            newArr[index].distance = 42
            setArr(newArr)
        } else if (value === 1) {
            newArr[index].type = 1
            newArr[index].distance = 21
            setArr(newArr)
        } else if (value === 2) {
            newArr[index].type = 2
            newArr[index].distance = 0
            setArr(newArr)
        }
    }

    const handleUpdateField = (e) => {
        if (e.target.name === 'rStatus') {
            setData({
                ...data,
                [e.target.name]: Number(e.target.value)
            })
        } else if (e.target.name === 'sStatus') {
            setData({
                ...data,
                [e.target.name]: Number(e.target.value)
            })
        } else {
            setData({
                ...data,
                [e.target.name]: e.target.value
            })
        }
    }

    const handleOptionStatusChange = (name, value) => {
        if (name === 'sStatus' && Number(value) && selectionOptions.length === 0) {
            setSelectionOptions([createDefaultSelectionOption(0)])
        }

        setData((prevData) => ({
            ...prevData,
            [name]: Number(value),
        }))
    }
    
    const handleUploadClick = (e, type) => {
        e.preventDefault()

        const file = e.target.files?.[0]

        if (!file) {
            return
        }

        if (!isAnimalImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(ANIMAL_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setData({
                ...data,
                [e.target.name]: file
            })
            if (type === 1) {
                setMainImage(reader.result)
            } else if (type === 2) {
                setSubImage1(reader.result)
            } else if (type === 3) {
                setRecordImage(reader.result)
            }
        }
    
        reader.readAsDataURL(file)
    }

    // 선택 이미지 취소는 저장 전 file input과 미리보기만 비운다. 기존 서버 이미지는 그대로 유지한다.
    const handleClearSelectedImage = (fieldName, inputId) => {
        setData((prev) => {
            const nextData = { ...prev }
            delete nextData[fieldName]
            return nextData
        })

        const input = document.getElementById(inputId)
        if (input) {
            input.value = ''
        }

        const fallbackImage = baseImagePreviewRef.current[fieldName] || NO_IMAGE
        if (fieldName === 'subImage1') {
            setSubImage1(fallbackImage)
        } else if (fieldName === 'recordImage') {
            setRecordImage(fallbackImage)
        }
    }

    const syncAnimalPackage = async (productId) => {
        const { data: detailResponse } = await API.post('/api/v1/admin/animal/getAnimal', { productId })

        if (detailResponse.code !== 1) {
            throw new Error(detailResponse.message || '패키지 상품 상세를 다시 불러오지 못했습니다.')
        }

        const regions = buildAnimalPackageRegions(detailResponse.categorys || [])

        if (!validateAnimalPackageRegions(regions)) {
            throw new Error('서울25 구 코스명을 확인해주세요.')
        }

        const { data: packageResponse } = await API.post('/api/v1/admin/animalPackage/upsert', {
            productId,
            packageCode: normalizeAnimalPackageCode(data.packageCode || ANIMAL_PACKAGE_CODE),
            progressPackageCode: normalizeAnimalPackageCode(data.progressPackageCode),
            requiredAreaM2: Number(data.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2),
            status: 1,
            regions,
        })

        if (![1, 200].includes(Number(packageResponse.code))) {
            throw new Error(packageResponse.message || '패키지 구 매핑 저장에 실패했습니다.')
        }
    }

    const updateData = async (params, { isPackageProduct = false } = {}) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/animal/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                const productId = data.data?.productId || params.get('productId')

                if (isPackageProduct) {
                    try {
                        await syncAnimalPackage(productId)
                        moveToList({ severity: 'success', message: '서울25 패키지 상품과 구 매핑을 저장했습니다.' })
                    } catch (packageError) {
                        moveToList({
                            severity: 'warning',
                            message: `상품은 저장됐지만 패키지 구 매핑 저장에 실패했습니다. ${packageError.message}`,
                        })
                    }
                    return
                }

                moveToList({ severity: 'success', message: data.message || '애니멀런을 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('애니멀런을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleComplete = (res) => {
        let fullAddress = res.address
        let extraAddress = ""

        if (res.addressType === "R") {
            if (res.bname !== "") {
                extraAddress += res.bname
            }
            if (res.buildingName !== "") {
                extraAddress += extraAddress !== "" ? `, ${res.buildingName}` : res.buildingName
            }
            fullAddress += extraAddress !== "" ? ` (${extraAddress})` : ""
        }

        data.country = res.sido
        data.area = res.sigungu
        if(res.sido.length > 2) {
            data.country = res.sido
        }
        
        data.address = fullAddress
        setOpenModal(false)

        fetch(`https://dapi.kakao.com/v2/local/search/address.json?query=${fullAddress}`, {
            method: 'GET',
            headers: { 
                "Content-Type": "application/json; charset=utf-8", 
                "Authorization": `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`
            }
        })
        .then(response => {
            return response.json()
        })
        .then(data => {
            data.latitude = data.latitude && data.latitude
            data.longitude = data.longitude && data.longitude
            if (!data.latitude) data.latitude = data.documents[0].y
            if (!data.longitude) data.longitude = data.documents[0].x
        })
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (data.title || '').trim()

        setData((prev) => ({
            ...prev,
            title: nextTitle
        }))

        if (arr.length === 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('코스를 등록해주세요.')
            return
        }

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회명을 입력해주세요.')
            return
        }

        if (Number(data.sStatus)) {
            const hasInvalidSelectionOption = selectionOptions.length === 0 || selectionOptions.some((item) => !String(item.name || '').trim())

            if (hasInvalidSelectionOption) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('선택 옵션명을 등록해주세요.')
                return
            }
        }

        const shippingPolicyError = validateAnimalShippingPolicy(data)

        if (shippingPolicyError) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(shippingPolicyError)
            return
        }

        const isPackageProduct = packageMode

        if (isPackageProduct) {
            const packageCode = normalizeAnimalPackageCode(data.packageCode)
            const progressPackageCode = normalizeAnimalPackageCode(data.progressPackageCode)

            if (!isValidAnimalPackageCode(packageCode)) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('패키지 코드는 영문 대문자, 숫자, 밑줄을 사용해 50자 이내로 입력해주세요.')
                return
            }

            if (packageCode === progressPackageCode) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('현재 패키지를 진행도 공유 기준으로 선택할 수 없습니다.')
                return
            }

            if (arr.filter((item) => Number(item.status || 0) === 0).length !== SEOUL25_REGIONS.length) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('서울25 패키지는 25개 구 코스가 필요합니다.')
                return
            }
        }

        let nextAddress = data.address
        let nextAddressDetail = data.addressDetail
        if(data.meetType === 2) {
            nextAddress = ''
        }
        if(data.meetType2 === 3) {
            nextAddress = ''
            nextAddressDetail = ''
        }

        const totalPayQuantity = arr.reduce((sum, item) => sum + toNumber(item.quantity), 0)
        const beginTime = new Date(beginDay)
        beginTime.setHours(0)
        beginTime.setMinutes(0)
        beginTime.setSeconds(0)
        const finishTime = new Date(finishDay)
        finishTime.setHours(23)
        finishTime.setMinutes(59)
        finishTime.setSeconds(59)
        const formData = new FormData()
        formData.append('productId',data.productId)
        formData.append('adminId',data.adminId)
        if (data.mainImage instanceof File) formData.append('mainImage',data.mainImage)
        if (data.subImage1 instanceof File) formData.append('subImage1',data.subImage1)
        if (data.recordImage instanceof File) formData.append('recordImage',data.recordImage)
        formData.append('meetType',data.meetType)
        formData.append('meetType2',data.meetType2)
        formData.append('title',nextTitle)
        formData.append('country',data.country)
        formData.append('area',data.area)
        formData.append('address',nextAddress)
        formData.append('addressDetail',nextAddressDetail)
        formData.append('latitude',data.latitude)
        formData.append('longitude',data.longitude)
        formData.append('startTime',data.startTime)
        formData.append('endTime',data.endTime)
        formData.append('beginTime', beginTime)
        formData.append('finishTime', finishTime)
        formData.append('info1',data.info1)
        formData.append('info2',data.info2)
        formData.append('info3',data.info3)
        formData.append('info4',data.info4)
        formData.append('info5',data.info5)
        formData.append('info6',data.info6)
        formData.append('info7',data.info7)
        formData.append('paymentStatus',data.paymentStatus)
        formData.append('couponStatus',data.couponStatus)
        formData.append('visibleStatus',data.visibleStatus)
        formData.append('payQuantity', totalPayQuantity)
        formData.append('basicLike',data.basicLike)
        formData.append('rStatus',data.rStatus)
        formData.append('sStatus',data.sStatus)
        formData.append('isShow',data.isShow)
        formData.append('shippingPolicyType', data.shippingPolicyType)
        formData.append('shippingPrice', data.shippingPrice)

        if (isPackageProduct) {
            formData.append('isAnimalPackage', 'true')
            formData.append('packageCode', normalizeAnimalPackageCode(data.packageCode || ANIMAL_PACKAGE_CODE))
            formData.append('progressPackageCode', normalizeAnimalPackageCode(data.progressPackageCode))
            formData.append('requiredAreaM2', data.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2)
        }

        getSubmittedCategoryRows().forEach((item, index) => formData.append(`categoryArr[${index}]`, JSON.stringify(item)))

        options.forEach((item, index) => formData.append(`optionArr[${index}]`, JSON.stringify(item)))

        updateData(formData, { isPackageProduct })
    }

    const handleClick = (e) => {
        e.target.select()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="애니멀런 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={handleBackClick} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            <AddressSearchModal open={openModal} onClose={() => setOpenModal(false)} onComplete={handleComplete} />
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>{packageMode ? '패키지 상품 수정' : '애니멀런 수정'}</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>애니멀런 수정</Title>
                    <AnimalDetailSummary
                        data={data}
                        beginDay={beginDay}
                        finishDay={finishDay}
                        totalLimitQuantity={totalLimitQuantity}
                        totalAvailableQuantity={totalAvailableQuantity}
                        formatQuantity={formatQuantity}
                    />
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                        <Prompt when={leaveGuardEnabled} message={ANIMAL_IMAGE_LEAVE_MESSAGE} />
                        <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        {packageMode && (
                            <FormSection
                                title="패키지 식별 및 진행도"
                                description="판매 상품별 고유 코드를 지정하고, 활성화 및 메달 진행도를 공유할 기준 패키지를 선택합니다."
                            >
                                <AnimalPackageSharingFields data={data} setData={setData} currentProductId={data.productId || id} />
                            </FormSection>
                        )}
                        <FormSection
                            title="코스 설정"
                            description={packageMode ? "서울25 25개 구 코스별 거리, 가격, 판매 수량을 수정합니다." : "코스별 거리, 가격, 판매 수량을 수정합니다."}
                        >
                            {arr.map((item, i) => {
                                return (
                                    item.status === 0 &&
                                    <Grid container spacing={2} sx={courseRowSx} key={i}>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="코스"
                                                    select
                                                    id={'categoryType'+i}
                                                    name={'categoryType'+i}
                                                    value={item.type}
                                                    onChange={(e) => onChangeOptionType(e, i)} 
                                                    size="small"
                                                >
                                                    <MenuItem value={0}>풀코스</MenuItem>
                                                    <MenuItem value={1}>하프코스</MenuItem>
                                                    <MenuItem value={2}>거리입력</MenuItem>
                                                </TextField>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="거리"
                                                    required
                                                    id={'categoryDistance'+i}
                                                    name={'categoryDistance'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                    value={item.distance}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].distance = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="가격"
                                                    required
                                                    id={'categoryPrice'+i}
                                                    name={'categoryPrice'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0 } }}
                                                    value={item.price}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].price = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="코스명"
                                                    id={'categoryName'+i}
                                                    name={'categoryName'+i}
                                                    value={item.name}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].name = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="수량"
                                                    required
                                                    id={'categoryQuantity'+i}
                                                    name={'categoryQuantity'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                                    value={item.quantity}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].quantity = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                    helperText={getQuantitySummaryText(item.quantity, item.netSoldQuantity, item.waitingDepositQuantity)}
                                                    FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        {i > 0 && 
                                            <Grid item xs={12} sm={6} md={2} >
                                                <Button variant="outlined" color="error" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={()=>removeInput(i)} startIcon={<RemoveIcon />}>코스삭제</Button>
                                            </Grid>
                                        }
                                    </Grid>
                                )
                            })}
                            <Box sx={{ mt: 1}}>
                                <Button variant="contained" color="success" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={addInput} startIcon={<Add />}>코스추가</Button>
                            </Box>
                        </FormSection>

                        <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'stretch' }}>
                        {options.length > 0 &&
                        <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
                        <FormSection
                            title="필수 옵션"
                            description="참가자가 반드시 선택해야 하는 옵션을 설정합니다."
                            sx={{ mb: 0, flex: 1 }}
                        >
                            <Grid container spacing={2} sx={{ marginTop: 0 }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <OptionStatusSetting
                                        label="필수옵션"
                                        value={data.rStatus}
                                        onChange={(value) => handleOptionStatusChange('rStatus', value)}
                                    />
                                </Grid>
                            </Grid>
                            {data.rStatus === 1 && options.map((row, i) => {
                                return (
                                    <Grid container spacing={2} sx={{ marginTop: 2 }} key={i}>
	                                        <Grid item xs={12} md={6} sm={6} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="옵션명"
                                                    required
                                                    id={'optionName'+i}
                                                    name={'optionName'+i}
                                                    value={row.name}
                                                    onChange={(e) => {
                                                        let newOption = [...options]
                                                        newOption[i].name = e.target.value
                                                        setOptions(newOption)
                                                    }}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
	                                        <Grid item xs={12} md={6} sm={6} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="수량"
                                                    required
                                                    id={'optionQuantity'+i}
                                                    name={'optionQuantity'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0 } }}
                                                    value={row.quantity}
                                                    onChange={(e) => {
                                                        let newOption = [...options]
                                                        newOption[i].quantity = e.target.value
                                                        setOptions(newOption)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )
                            })}
                        </FormSection>
                        </Grid>
                        }

                        <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
                        <FormSection
                            title="선택 옵션"
                            description="추가 구매 가능한 옵션을 설정합니다."
                            sx={{ mb: 0, flex: 1 }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <OptionStatusSetting
                                        label="선택옵션"
                                        value={data.sStatus}
                                        onChange={(value) => handleOptionStatusChange('sStatus', value)}
                                    />
                                </Grid>
                            </Grid>
                            {Number(data.sStatus) === 1 && (
                                <Box sx={{ mt: 2 }}>
                                    {selectionOptions.map((row, i) => (
                                        <Box key={row.id ?? row.productCategoryId ?? i} sx={courseRowSx}>
                                            <Grid container spacing={1.5} alignItems="flex-start">
                                                <Grid item xs={12} md={3}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="옵션명"
                                                            required
                                                            value={row.name}
                                                            onChange={(e) => updateSelectionOption(i, 'name', e.target.value)}
                                                            size="small"
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="가격"
                                                            required
                                                            type="number"
                                                            InputProps={{ inputProps: { min: 0 } }}
                                                            value={row.price}
                                                            onChange={(e) => updateSelectionOption(i, 'price', e.target.value)}
                                                            onClick={handleClick}
                                                            size="small"
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="수량"
                                                            required
                                                            type="number"
                                                            InputProps={{ inputProps: { min: 0 } }}
                                                            value={row.quantity}
                                                            onChange={(e) => updateSelectionOption(i, 'quantity', e.target.value)}
                                                            onClick={handleClick}
                                                            size="small"
                                                            helperText={getQuantitySummaryText(row.quantity, row.netSoldQuantity, row.waitingDepositQuantity)}
                                                            FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            select
                                                            label="웹 노출"
                                                            value={Number(row.webVisible ?? 1)}
                                                            onChange={(e) => updateSelectionOption(i, 'webVisible', Number(e.target.value))}
                                                            size="small"
                                                        >
                                                            {VISIBILITY_OPTIONS.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} md={2}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            select
                                                            label="앱 노출"
                                                            value={Number(row.appVisible ?? 0)}
                                                            onChange={(e) => updateSelectionOption(i, 'appVisible', Number(e.target.value))}
                                                            size="small"
                                                        >
                                                            {VISIBILITY_OPTIONS.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={1}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<RemoveIcon />}
                                                        disabled={selectionOptions.length <= 1}
                                                        onClick={() => removeSelectionOption(i)}
                                                        sx={FORM_INLINE_ACTION_BUTTON_SX}
                                                    >
                                                        삭제
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                    <Box sx={{ mt: 1.5 }}>
                                        <Button variant="outlined" startIcon={<Add />} onClick={addSelectionOption} sx={FORM_INLINE_ACTION_BUTTON_SX}>
                                            선택옵션 추가
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </FormSection>
                        </Grid>
                        </Grid>

                        <FormSection
                            title="이미지"
                            description={packageMode ? "메인이미지와 상세이미지를 등록합니다. 완주기록증이미지는 패키지 상품에서 비활성화됩니다." : "메인이미지, 상세이미지, 완주기록증이미지를 한 줄에서 확인하고 교체합니다."}
                            sx={{ mt: 2 }}
                        >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4} md={4}>
                                <ImageUploadCard
                                    title="메인이미지"
                                    image={mainImage}
                                    imageAlt="메인이미지"
                                    inputId="mainImage"
                                    inputName="mainImage"
                                    uploadType={1}
                                    onUpload={handleUploadClick}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={4}>
                                <ImageUploadCard
                                    title="상세이미지"
                                    image={subImage1}
                                    imageAlt="상세이미지"
                                    inputId="subImage1"
                                    inputName="subImage1"
                                    uploadType={2}
                                    onUpload={handleUploadClick}
                                    canClear
                                    hasPendingFile={data.subImage1 instanceof File}
                                    onClear={() => handleClearSelectedImage('subImage1', 'subImage1')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={4}>
                                <ImageUploadCard
                                    title="완주기록증이미지"
                                    image={recordImage}
                                    imageAlt="완주기록증이미지"
                                    inputId="recordImage"
                                    inputName="recordImage"
                                    uploadType={3}
                                    onUpload={handleUploadClick}
                                    canClear
                                    hasPendingFile={data.recordImage instanceof File}
                                    onClear={() => handleClearSelectedImage('recordImage', 'recordImage')}
                                    disabled={packageMode}
                                />
                            </Grid>
                        </Grid>
                        </FormSection>

                        <FormSection
                            title="일정"
                            description="대회 타입과 접수/진행 기간을 설정합니다."
                        >
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12} sm={12} >
                                <FormControl fullWidth>
                                    <TextField
                                        label="대회타입"
                                        select
                                        id="meetType2"
                                        name="meetType2"
                                        value={data.meetType2}
                                        onChange={handleUpdateField} 
                                        size="small"
                                    >
                                        <MenuItem value=''>전체</MenuItem>
                                        <MenuItem value='1'>애니멀런</MenuItem>
                                        <MenuItem value='2'>댕댕이레이스</MenuItem>
                                        <MenuItem value='3'>산넘어산</MenuItem>
                                        <MenuItem value='4'>B급마라톤</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}  locale={koLocale}>
                                        <DesktopDatePicker
                                            label="접수시작일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={data.startTime}
                                            onChange={d => setData({
                                                ...data,
                                                startTime: d
                                            })}
                                            renderInput={(params) => <TextField required {...params} size="small"/>}
                                        />
                                    </LocalizationProvider>
                                    
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="접수종료일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={data.endTime}
                                            minDate={new Date(data.startTime)}
                                            onChange={d => setData({
                                                ...data,
                                                endTime: d
                                            })}
                                            renderInput={(params) => <TextField required {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="대회시작일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={beginDay}
                                            // minDate={new Date(data.endTime)}
                                            onChange={d => setBeginDay(d)}
                                            renderInput={(params) => <TextField required {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            {/* <Grid item xs={3} md={3} sm={6}>
                                <FormControl sx={{ width: '10ch' }}>
                                    <TextField
                                        label="시"
                                        select
                                        id="beginHours"
                                        name="beginHours"
                                        value={beginHours}
                                        onChange={(e) => setBeginHours(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value={0}>0</MenuItem>
                                        <MenuItem value={1}>1</MenuItem>
                                        <MenuItem value={2}>2</MenuItem>
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={4}>4</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={6}>6</MenuItem>
                                        <MenuItem value={7}>7</MenuItem>
                                        <MenuItem value={8}>8</MenuItem>
                                        <MenuItem value={9}>9</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={11}>11</MenuItem>
                                        <MenuItem value={12}>12</MenuItem>
                                        <MenuItem value={13}>13</MenuItem>
                                        <MenuItem value={14}>14</MenuItem>
                                        <MenuItem value={15}>15</MenuItem>
                                        <MenuItem value={16}>16</MenuItem>
                                        <MenuItem value={17}>17</MenuItem>
                                        <MenuItem value={18}>18</MenuItem>
                                        <MenuItem value={19}>19</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={21}>21</MenuItem>
                                        <MenuItem value={22}>22</MenuItem>
                                        <MenuItem value={23}>23</MenuItem>
                                    </TextField>
                                </FormControl>
                                <FormControl sx={{ width: '10ch' }}>
                                    <TextField
                                        label="분"
                                        select
                                        id="beginMinutes"
                                        name="beginMinutes"
                                        value={beginMinutes}
                                        onChange={(e) => setBeginMinutes(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value={0}>0</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={30}>30</MenuItem>
                                        <MenuItem value={40}>40</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid> */}
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="대회종료일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={finishDay}
                                            minDate={beginDay}
                                            onChange={d => setFinishDay(d)}
                                            renderInput={(params) => <TextField required {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            {/* <Grid item xs={3} md={3} sm={6}>
                                <FormControl sx={{ width: '10ch' }}>
                                    <TextField
                                        label="시"
                                        select
                                        id="finishHours"
                                        name="finishHours"
                                        value={finishHours}
                                        onChange={(e) => setFinishHours(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value={0}>0</MenuItem>
                                        <MenuItem value={1}>1</MenuItem>
                                        <MenuItem value={2}>2</MenuItem>
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={4}>4</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={6}>6</MenuItem>
                                        <MenuItem value={7}>7</MenuItem>
                                        <MenuItem value={8}>8</MenuItem>
                                        <MenuItem value={9}>9</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={11}>11</MenuItem>
                                        <MenuItem value={12}>12</MenuItem>
                                        <MenuItem value={13}>13</MenuItem>
                                        <MenuItem value={14}>14</MenuItem>
                                        <MenuItem value={15}>15</MenuItem>
                                        <MenuItem value={16}>16</MenuItem>
                                        <MenuItem value={17}>17</MenuItem>
                                        <MenuItem value={18}>18</MenuItem>
                                        <MenuItem value={19}>19</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={21}>21</MenuItem>
                                        <MenuItem value={22}>22</MenuItem>
                                        <MenuItem value={23}>23</MenuItem>
                                    </TextField>
                                </FormControl>
                                <FormControl sx={{ width: '10ch' }}>
                                    <TextField
                                        label="분"
                                        select
                                        id="finishMinutes"
                                        name="finishMinutes"
                                        value={finishMinutes}
                                        onChange={(e) => setFinishMinutes(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value={0}>0</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={30}>30</MenuItem>
                                        <MenuItem value={40}>40</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid> */}
                        </Grid>
                        </FormSection>

                        <FormSection
                            title="대회 정보"
                            description="운영자가 앱에 노출할 기본 문구와 문의 정보를 입력합니다."
                        >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="대회명"
                                        required
                                        id="title"
                                        name="title"
                                        value={data.title}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            { data.meetType === 2 && 
                                <>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="국가명"
                                            required
                                            id="country"
                                            name="country"
                                            value={data.country}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="도시"
                                            required
                                            id="area"
                                            name="area"
                                            value={data.area}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                </>
                            }
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="주소"
                                        id="address"
                                        name="address"
                                        value={data.address}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="상세주소"
                                        id="addressDetail"
                                        name="addressDetail"
                                        value={data.addressDetail}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="문의"
                                        id="info2"
                                        name="info2"
                                        value={data.info2}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="주최"
                                        id="info4"
                                        name="info4"
                                        value={data.info4}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="홈페이지"
                                        id="info1"
                                        name="info1"
                                        value={data.info1}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="협찬"
                                        id="info3"
                                        name="info3"
                                        value={data.info3}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        </FormSection>

                        <FormSection
                            title="배송 정책"
                            description="상품별 배송비와 무료배송 조건을 설정합니다. 코스별이 아닌 productId별로 적용됩니다."
                        >
                            <AnimalShippingPolicyFields data={data} setData={setData} />
                        </FormSection>

                        <FormSection
                            title="판매/노출"
                            description="결제, 쿠폰, 판매 수량, 노출 여부를 설정합니다."
                            sx={{ mb: 0 }}
                        >
                        <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 10 }}>
                            <Grid item xs={1} sm={1} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="결제가능"
                                        select
                                        id="paymentStatus"
                                        name="paymentStatus"
                                        value={data.paymentStatus}
                                        onChange={handleUpdateField}
                                        size="small"
                                    >
                                        <MenuItem value={1}>가능</MenuItem>
                                        <MenuItem value={0}>불가능</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="쿠폰가능"
                                        select
                                        id="couponStatus"
                                        name="couponStatus"
                                        value={data.couponStatus}
                                        onChange={handleUpdateField}
                                        size="small"
                                    >
                                        <MenuItem value={1}>가능</MenuItem>
                                        <MenuItem value={0}>불가능</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="전체 판매제한수량"
                                        type="number"
                                        id="payQuantitySummary"
                                        value={totalLimitQuantity}
                                        disabled
                                        size="small"
                                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                                        helperText={getQuantitySummaryText(totalLimitQuantity, data.netSoldQuantity, data.waitingDepositQuantity)}
                                        FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="기본좋아요수"
                                        type="number"
                                        id="basicLike"
                                        name="basicLike"
                                        value={data.basicLike}
                                        onChange={handleUpdateField}
                                        onClick={handleClick}
                                        size="small"
                                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={1} sm={1} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="노출여부"
                                        select
                                        id="isShow"
                                        name="isShow"
                                        value={data.isShow}
                                        onChange={handleUpdateField}
                                        size="small"
                                    >
                                        <MenuItem value={1}>노출</MenuItem>
                                        <MenuItem value={0}>미노출</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                        </Grid>
                        </FormSection>
                        </fieldset>
                        <BottomButtonGroup
                            variant="formAction"
                            visible={true}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            backDisabled={saving}
                            onBackClick={handleBackClick}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
